// FeedContainer.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/config/axiosConfig";
import { motion } from "framer-motion";
import { formatCreatedAtAgo, formatImageUrl } from "@/lib/general";
import { useNavigate } from "react-router-dom";
import { formatTimeToHHMM } from "@/lib/general";
import { HomeIcon, MapIcon, UserIcon } from "@heroicons/react/20/solid";
import { BellSlashIcon } from "@heroicons/react/24/outline";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { DateTime } from "luxon";
import { ServerFlightInfo } from "@/types/flights";
import { Plane } from "lucide-react";
import useAuthStore from "@/stores/user/authStore";
import { AvatarImage } from "@/components/ui/DefaultAvatar";

type LocationUpdateType =
  | "experience_added"
  | "experience_removed"
  | "experience_updated"
  | "accommodation_added"
  | "accommodation_removed"
  | "accommodation_updated"
  | "transport_added"
  | "transport_removed"
  | "transport_updated"
  | "participants_changed";

interface LocationUpdateMetadataBase {
  itemName?: string;
  itemType?: string;
}

interface ExperienceMetadata extends LocationUpdateMetadataBase {
  duration?: number | null;
  rating?: number;
  images?: string[];
}

interface AccommodationMetadata extends LocationUpdateMetadataBase {
  lodging: {
    id: number;
    name: string;
    type: string;
    address: string;
    pricing: {
      amount: number;
      currency: string;
    };
    moreInfo: {
      reviewCount: number;
      reviewScore: number;
    };
    policies: {
      checkIn: string;
      checkOut: string;
    };
    coordinates: {
      latitude: number;
      longitude: number;
    };
    description: string;
    mainImageURL: string;
    additional_images: string[];
  };
  timeZone: string;
}

interface ParticipantsMetadata extends LocationUpdateMetadataBase {
  added: boolean;
  participants: {
    id: string;
    name: string;
    waytora_avatar_url: string;
  }[];
}

interface TransportMetadata extends LocationUpdateMetadataBase {
  flight: ServerFlightInfo;
}

type LocationUpdateMetadata =
  | ExperienceMetadata
  | AccommodationMetadata
  | ParticipantsMetadata;

interface FeedUpdate {
  update_id: string;
  trip_id: string;
  waytora_id: string;
  location_user_id: string;
  name: string;
  profile_image: string;
  created_at: string;
  type: LocationUpdateType;
  metadata: LocationUpdateMetadata;
  trip: {
    id: string;
    name: string;
  };
}

const getFeedPosts = async (): Promise<FeedUpdate[]> => {
  const response = await api.social.get<FeedUpdate[]>("/feed");
  const feedItems = response.data;
  feedItems.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  return feedItems;
};

const renderUpdate = (
  update: FeedUpdate,
  handleTripClick: (tripId: string) => void,
  handleUserClick: (user_id: string) => void
) => {
  if (
    update.type === "experience_removed" ||
    update.type === "accommodation_removed" ||
    update.type === "transport_removed"
  ) {
    return null;
  }

  const {
    name,
    profile_image,
    created_at,
    type,
    metadata,
    waytora_id,
    location_user_id: user_id,
  } = update;

  const baseCardStyle =
    "border border-neutral-200 rounded-lg px-3.5 py-4 bg-white mb-4";
  const userInfoStyle = "flex flex-row space-x-1.5 items-center mb-2";
  const avatarStyle =
    "rounded-full w-6 h-6 object-cover border border-neutral-200";
  const usernameStyle =
    "text-xs font-medium hover:text-blue-600 transition-colors duration-200";
  const timestampStyle = "text-xs text-neutral-500 font-light";

  const renderContent = () => {
    switch (type) {
      case "experience_added": {
        const expMeta = metadata as ExperienceMetadata;
        return (
          <div className="py-2">
            <p className="text-neutral-700 text-[13px] 2xl:text-[14px] leading-relaxed pb-2">
              <span className="inline-flex items-center gap-1.5">
                <span className="font-normal">Added</span>{" "}
                <div className="flex justify-center items-center ring-1 ring-neutral-600 rounded-full w-5 h-5">
                  <MapIcon className="w-3 h-3 text-neutral-600" />
                </div>
                <span className="font-medium"> {expMeta.itemName}</span>
              </span>{" "}
              to{" "}
              <button
                onClick={() => handleTripClick(update.trip_id)}
                className="font-semibold text-indigo-700 hover:text-indigo-500 transition-colors duration-200"
              >
                {update.trip.name}
              </button>
            </p>
            {expMeta.images && expMeta.images.length > 1 && (
              <div className="w-full h-56 grid grid-cols-3 gap-2 mt-3">
                {expMeta.images.slice(0, 3).map((image, index) => (
                  <motion.div
                    key={index}
                    className="relative h-full overflow-hidden rounded-lg"
                    transition={{ duration: 0.2 }}
                  >
                    <LazyLoad
                      src={formatImageUrl(image, "high")}
                      lowResSrc={formatImageUrl(image, "medium")}
                      alt={`${expMeta.itemName} image ${index + 1}`}
                      className="rounded-lg w-full h-full"
                      objectFit="cover"
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        );
      }
      case "accommodation_added": {
        const accMeta = metadata as AccommodationMetadata;
        return (
          <div className="py-2">
            <p className="text-neutral-700 text-[13px] 2xl:text-[14px] leading-relaxed pb-2">
              <span className="inline-flex items-center gap-1.5">
                <span className="font-normal">Added</span>{" "}
                <div className="flex justify-center items-center ring-1 ring-neutral-600 rounded-full w-5 h-5">
                  <HomeIcon className="w-3 h-3 text-neutral-600" />
                </div>
                <span className="font-medium"> {accMeta.lodging.name}</span>
              </span>{" "}
              to{" "}
              <button
                onClick={() => handleTripClick(update.trip_id)}
                className="font-semibold text-indigo-700 hover:text-indigo-500 transition-colors duration-200"
              >
                {update.trip.name}
              </button>{" "}
              from{" "}
              <span className="font-normal">
                {DateTime.fromISO(accMeta.lodging.policies.checkIn, {
                  zone: accMeta.timeZone,
                }).toFormat("d MMMM")}{" "}
                to{" "}
                {DateTime.fromISO(accMeta.lodging.policies.checkOut, {
                  zone: accMeta.timeZone,
                }).toFormat("d MMMM")}
              </span>
            </p>
            {accMeta.lodging.additional_images?.length > 1 && (
              <div className="w-full h-56 grid grid-cols-3 gap-2 mt-3">
                {accMeta.lodging.additional_images
                  .slice(0, 3)
                  .map((image, index) => (
                    <motion.div
                      key={index}
                      className="relative h-full overflow-hidden rounded-lg"
                      transition={{ duration: 0.2 }}
                    >
                      <LazyLoad
                        src={formatImageUrl(image, "high")}
                        lowResSrc={formatImageUrl(image, "medium")}
                        alt={`${accMeta.lodging.name} image ${index + 1}`}
                        className="rounded-lg w-full h-full"
                        objectFit="cover"
                      />
                    </motion.div>
                  ))}
              </div>
            )}
          </div>
        );
      }
      case "participants_changed": {
        const partMeta = metadata as ParticipantsMetadata;
        return (
          <p className="py-1 text-neutral-700 text-[13px] 2xl:text-[14px] leading-relaxed">
            <span className="inline-flex items-center gap-1.5">
              <span className="font-normal">
                {partMeta.added ? "Added" : "Removed"}
              </span>{" "}
              <div className="flex justify-center items-center ring-1 ring-neutral-600 rounded-full w-5 h-5">
                <UserIcon className="w-3 h-3 text-neutral-600" />
              </div>
              <span className="font-medium">
                {partMeta.participants.map((participant, index) => (
                  <React.Fragment key={participant.id}>
                    {participant.name}
                    {index < partMeta.participants.length - 1 && ", "}
                  </React.Fragment>
                ))}
              </span>
            </span>{" "}
            {partMeta.participants.length > 1
              ? "as participants to "
              : "as a participant to "}{" "}
            <button
              onClick={() => handleTripClick(update.trip_id)}
              className="font-semibold text-indigo-700 hover:text-indigo-500 transition-colors duration-200"
            >
              {update.trip.name}
            </button>
          </p>
        );
      }
      case "transport_added": {
        const transMeta = metadata as TransportMetadata;
        const uniqueAirlines = [
          ...new Set(transMeta.flight.route.map((leg) => leg.short_airline)),
        ];
        return (
          <div className="mt-2">
            <p className="mt-2 text-neutral-700 text-[13px] 2xl:text-[14px] leading-relaxed">
              Added a flight from {transMeta.flight.from.city} to{" "}
              {transMeta.flight.to.city} for trip{" "}
              <button
                onClick={() => handleTripClick(update.trip_id)}
                className="font-semibold text-indigo-700 hover:text-indigo-500 transition-colors duration-200"
              >
                {update.trip.name}
              </button>
            </p>
            <div className="bg-neutral-100/70 rounded-lg py-3 px-4 w-full mt-3">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center space-x-4">
                  <Plane
                    className={`w-5 h-5 text-neutral-600 ${
                      transMeta.flight.stops === 0
                        ? "transform rotate-45"
                        : "transform -rotate-45"
                    }`}
                  />
                  <div className="flex flex-col items-start">
                    <p className="text-sm 2xl:text-[14px] text-neutral-700 font-medium mb-0.5">
                      <span className="text-neutral-700">
                        {transMeta.flight.from.city}
                      </span>
                      <span className="text-sm 2xl:text-[14px] text-neutral-400 font-normal ml-1.5">
                        {transMeta.flight.from.airport}
                      </span>
                      <span className="text-neutral-700 mx-2">→</span>
                      <span className="text-neutral-700">
                        {transMeta.flight.to.city}
                      </span>
                      <span className="text-sm 2xl:text-[14px] text-neutral-400 font-normal ml-1.5">
                        {transMeta.flight.to.airport}
                      </span>
                    </p>
                    <p className="text-xs 2xl:text-[13px] text-neutral-500 font-light">
                      Arriving into {transMeta.flight.to.city} at{" "}
                      {formatTimeToHHMM(transMeta.flight.arrival_local)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    {uniqueAirlines.map((airline, index) => (
                      <img
                        key={index}
                        src={`https://images.kiwi.com/airlines/64x64/${airline}.png`}
                        alt={airline}
                        className="w-8 h-8 rounded-md"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <motion.div className={baseCardStyle}>
      <div className={userInfoStyle}>
        <button
          onClick={() => handleUserClick(user_id)}
          className="flex flex-row space-x-2 items-center group"
        >
          <AvatarImage
            src={profile_image || ""}
            firstName={name.split(" ")[0] || ""}
            lastName={name.split(" ")[1] || ""}
            className={avatarStyle}
            alt={name}
          />
          <h4 className={usernameStyle}>{name}</h4>
        </button>
        <div className="flex flex-row space-x-1 items-center">
          <p className="text-xs 2xl:text-sm text-neutral-500">•</p>
          <p className={timestampStyle}>{formatCreatedAtAgo(created_at)}</p>
        </div>
      </div>
      {renderContent()}
    </motion.div>
  );
};

// Skeleton loaders for each content type
const SkeletonUserInfo = () => (
  <div className="flex flex-row space-x-1.5 items-center mb-2">
    <div className="rounded-full w-6 h-6 bg-gray-200 animate-pulse"></div>
    <div className="h-3 bg-gray-200 rounded w-24 animate-pulse"></div>
    <div className="flex flex-row space-x-1 items-center">
      <p className="text-xs 2xl:text-sm text-gray-200">•</p>
      <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
    </div>
  </div>
);

const SkeletonExperience = () => (
  <div className="py-2">
    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
    <div className="w-full h-56 grid grid-cols-3 gap-2 mt-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="relative h-full overflow-hidden rounded-lg bg-gray-200 animate-pulse"
        ></div>
      ))}
    </div>
  </div>
);

const SkeletonAccommodation = () => (
  <div className="py-2">
    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse mb-2"></div>
    <div className="w-full h-56 grid grid-cols-3 gap-2 mt-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="relative h-full overflow-hidden rounded-lg bg-gray-200 animate-pulse"
        ></div>
      ))}
    </div>
  </div>
);

const SkeletonParticipants = () => (
  <div className="py-2">
    <div className="h-4 bg-gray-200 rounded w-2/3 animate-pulse"></div>
  </div>
);

const SkeletonTransport = () => (
  <div className="mt-2">
    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
    <div className="bg-gray-100/70 rounded-lg py-3 px-4 w-full mt-3">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center space-x-4">
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div className="flex flex-col items-start">
            <div className="h-4 bg-gray-200 rounded w-48 animate-pulse mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-36 animate-pulse"></div>
          </div>
        </div>
        <div className="flex flex-row items-center space-x-2">
          <div className="w-8 h-8 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

const SkeletonFeedItem = ({ type }: { type: string }) => {
  const baseCardStyle =
    "border border-neutral-200 rounded-lg px-3.5 py-4 bg-white mb-4";

  return (
    <motion.div className={baseCardStyle}>
      <SkeletonUserInfo />
      {type === "experience" && <SkeletonExperience />}
      {type === "accommodation" && <SkeletonAccommodation />}
      {type === "participants" && <SkeletonParticipants />}
      {type === "transport" && <SkeletonTransport />}
    </motion.div>
  );
};

const getRandomSkeletonFeed = (count: number = 5) => {
  const contentTypes = [
    "experience",
    "accommodation",
    "participants",
    "transport",
  ];
  const skeletons = [];

  for (let i = 0; i < count; i++) {
    const randomType =
      contentTypes[Math.floor(Math.random() * contentTypes.length)];
    skeletons.push(<SkeletonFeedItem key={i} type={randomType || ""} />);
  }

  return skeletons;
};

const FeedContainer: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const {
    data: feedPosts,
    isLoading: isFeedLoading,
    error: feedError,
  } = useQuery({
    queryKey: ["feedPosts"],
    queryFn: getFeedPosts,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 1,
  });

  if (isFeedLoading) {
    return <div className="w-full space-y-4">{getRandomSkeletonFeed()}</div>;
  }

  if (feedError) {
    return <div>Error loading feed</div>;
  }

  if (!feedPosts || feedPosts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="bg-neutral-50 rounded-full p-4 mb-4">
          <BellSlashIcon className="w-8 h-8 text-neutral-500" />
        </div>
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          No updates yet
        </h3>
        <p className="text-neutral-500 text-center text-sm max-w-xs font-light">
          Start planning a trip or follow other travelers to see their updates
          here.
        </p>
      </div>
    );
  }

  const handleTripClick = (tripId: string) => {
    navigate(`/trip-builder/${tripId}`);
  };

  const handleUserClick = (user_id: string) => {
    if (user_id === currentUser?.user_id) {
      navigate(`/account`);
    } else {
      navigate(`/account/${user_id}`);
    }
  };

  return (
    <div className="w-full space-y-4">
      {feedPosts.map((update) => (
        <React.Fragment key={update.update_id}>
          {renderUpdate(update, handleTripClick, handleUserClick)}
        </React.Fragment>
      ))}
    </div>
  );
};

export default FeedContainer;
